/**
 * Detection + guarded recovery for failed dynamic imports of lazy-loaded chunks.
 *
 * When a new version is deployed, the previous build's content-hashed chunk
 * filenames are removed from the server. A browser tab still running the old
 * build holds the old lazy-route map, so navigating to a not-yet-loaded route
 * requests a chunk that now 404s — surfacing as:
 *
 *   TypeError: Failed to fetch dynamically imported module: .../chunk-XXXX.js
 *
 * This module turns that dead end into a single, guarded full-page reload onto
 * the intended URL, which pulls the fresh index.html + the new chunk map.
 *
 * SCOPE: this is the frontend SAFETY NET (Layer 1). The root cause is the deploy
 * deleting old immutable chunks with no overlap window; the durable cure is
 * retaining them for a grace period on the CDN/host (Layer 2 — infra). See
 * docs/chunk-load-recovery.md.
 *
 * The functions here are deliberately free of Angular DI so they can be unit
 * tested in isolation and reused from both the router's navigation-error handler
 * and the global ErrorHandler (which catches @defer / non-navigation imports).
 */

/**
 * Substrings identifying a failed dynamic `import()`. The wording differs per
 * engine, so all known variants are matched (compared lower-cased):
 *   Chromium : "Failed to fetch dynamically imported module: <url>"
 *   Firefox  : "error loading dynamically imported module: <url>"
 *   WebKit   : "Importing a module script failed."
 */
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
] as const;

/** How long a reload for a given URL is remembered, to break reload loops. */
export const RELOAD_GUARD_TTL_MS = 20_000;

/** sessionStorage key prefix for the per-URL reload guard. */
const RELOAD_GUARD_PREFIX = 'chunkReload:';

/** True when `error` (any shape) is a failed dynamic import of a lazy chunk. */
export function isChunkLoadError(error: unknown): boolean {
  const message = extractMessage(error).toLowerCase();
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function extractMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return '';
}

/**
 * Seams over the browser globals so the recovery logic is fully unit-testable
 * (fake clock, in-memory storage, spy reload) without a DOM.
 */
export interface ChunkReloadHooks {
  now(): number;
  isOnline(): boolean;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  reload(url: string): void;
}

const defaultHooks: ChunkReloadHooks = {
  now: () => Date.now(),
  isOnline: () => (typeof navigator === 'undefined' ? true : navigator.onLine),
  getItem: (key) => safeSessionGet(key),
  setItem: (key, value) => safeSessionSet(key, value),
  reload: (url) => window.location.assign(url),
};

/**
 * Attempt a one-shot recovery reload onto `targetUrl`.
 *
 * Returns `true` when a reload was triggered, `false` when suppressed because:
 *  - the browser is offline (a reload would fail too and could trap the user), or
 *  - this URL was already reloaded within {@link RELOAD_GUARD_TTL_MS} — meaning
 *    the chunk is genuinely missing/broken, not merely stale, so we stop rather
 *    than loop and let the error surface.
 *
 * The guard lives in sessionStorage (per-tab, cleared when the tab closes) and
 * is time-boxed, so a genuine future deploy-skew on the same URL can still
 * recover once the window elapses. We deliberately do NOT clear the guard on a
 * successful load: the time-box already guarantees at most one reload per URL
 * per window, which is the stronger loop protection.
 */
export function recoverFromChunkError(
  targetUrl: string,
  hooks: ChunkReloadHooks = defaultHooks,
): boolean {
  if (!hooks.isOnline()) return false;

  const key = RELOAD_GUARD_PREFIX + targetUrl;
  const last = Number(hooks.getItem(key) ?? '0');
  if (last > 0 && hooks.now() - last < RELOAD_GUARD_TTL_MS) {
    return false;
  }

  hooks.setItem(key, String(hooks.now()));
  hooks.reload(targetUrl);
  return true;
}

function safeSessionGet(key: string): string | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(key);
  } catch {
    // sessionStorage can throw in private mode / when disabled — degrade to "no
    // guard recorded" rather than break recovery.
    return null;
  }
}

function safeSessionSet(key: string, value: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, value);
  } catch {
    // Ignore — the reload still proceeds; only the loop guard is weakened.
  }
}
